import ValuationData, {
  PropertyValuationStepProperties,
  BuildingValuationStepProperties,
  LocalityValuationStepProperties,
  UnitValuationStepProperties,
} from '@/interfaces/valuation/ValuationData'
import {
  ValuationStep,
  ValuationStepName,
} from '@/interfaces/valuation/ValuationStep'
import PropertyValuationStepValidator from './validators/PropertyValuationStepValidator'
import BuildingValuationStepValidator from './validators/BuildingValuationStepValidator'
import LocalityValuationStepValidator from './validators/LocalityValuationStepValidator'
import UnitValuationStepValidator from './validators/UnitValuationStepValidator'
import { getNestedValue } from '@/util/helpers'
import { LocationType } from '@/interfaces/valuation/types/LocalityValuationStep'
import { PropertyType } from '@/interfaces/valuation/types/PropertyValuationStep'

export default class ValuationService {
  public static stepNames: string[] = Object.values(ValuationStepName)
  public static steps: ValuationStep[] = [
    {
      name: ValuationStepName.Property,
      title: 'Vyberte typ nemovitosti',
      data: {},
      validator: new PropertyValuationStepValidator(),
    },
    {
      name: ValuationStepName.Building,
      title: 'Vyplňte údaje o budově',
      data: {},
      validator: new BuildingValuationStepValidator(),
    },
    {
      name: ValuationStepName.Locality,
      title: 'Zadejte lokalitu',
      data: {},
      validator: new LocalityValuationStepValidator(),
    },
    {
      name: ValuationStepName.Unit,
      title: 'Upřesněte údaje o jednotce',
      data: {},
      validator: new UnitValuationStepValidator(),
    },
  ]
  public static valuationData: ValuationData = {
    steps: ValuationService.steps,
    previousStep: undefined,
    currentStep: ValuationService.steps[0],
    nextStep: ValuationService.steps[1],
    parentValuationId: null,
  }

  /**
   * Retrieves ValuationData object, if there are data in cache, it retrieves them for specific steps
   *
   * @return {ValuationData}
   */
  public static getValuationData(withCache = true): ValuationData {
    const valuationData = ValuationService.valuationData
    if (withCache) {
      for (const step of valuationData.steps) {
        step.data =
          JSON.parse(localStorage.getItem(`${step.name}`)!) || step.data
      }
    }

    return valuationData
  }

  /**
   * Caches ValuationStepData for each step into localStorage
   *
   * @param {ValuationData} data
   */
  public static setDataToCache(data: ValuationData): void {
    for (const step of data.steps) {
      localStorage.setItem(`${step.name}`, JSON.stringify(step.data))
    }
  }

  /**
   * Removes ValuationStepData from cache
   */
  public static removeDataFromCache(): void {
    for (const step of ValuationService.steps) {
      localStorage.removeItem(step.name)
    }
  }

  public static resetData(data: ValuationData): ValuationData {
    for (const step of data.steps) {
      if (step.name === ValuationStepName.Property) {
        continue
      }
      step.data = {}
    }

    return data
  }

  /**
   * Get next url in line or return the same on undefined
   *
   * @param {string} current Current step name in URL
   * @param {boolean} back Determines the direction (back/forward)
   *
   * @return {ValuationStepName} StepName for url
   */
  public static getNextStepUrl(current: string, back: boolean = false): string {
    for (let i = 0; i < ValuationService.stepNames.length; i++) {
      const stepName = ValuationService.stepNames[i]
      if (stepName === current) {
        const nextStepUrl = back
          ? ValuationService.stepNames[i - 1]
          : ValuationService.stepNames[i + 1]

        if (nextStepUrl) {
          return nextStepUrl
        }
        break
      }
    }

    return current
  }

  /**
   * Changes current/next/previous steps based on this step
   *
   * @param {ValuationStepName} stepName Step which we want to access
   * @param {ValuationData} data
   *
   * @return {ValuationData}
   */
  public static goToStep(
    stepName: ValuationStepName,
    data: ValuationData
  ): ValuationData {
    // save current data to steps
    for (let i = 0; i < data.steps.length; i++) {
      const step = data.steps[i]

      if (step.name === data.currentStep.name) {
        data.steps[i] = data.currentStep
        break
      }
    }

    // reorganize steps
    for (let i = 0; i < data.steps.length; i++) {
      const step = data.steps[i]

      if (step.name === stepName) {
        data.previousStep = data.steps[i - 1]
        data.currentStep = data.steps[i]
        data.nextStep = data.steps[i + 1]

        // reset validation on all upcoming steps
        for (let j = i + 1; j < data.steps.length; j++) {
          data.steps[j].validator.isValid = false
        }

        break
      }
    }

    return data
  }

  public static recreateValuationData(data: any): ValuationData {
    const valuationData = ValuationService.getValuationData(false)
    const stepMap = {
      [ValuationStepName.Property]: PropertyValuationStepProperties,
      [ValuationStepName.Building]: BuildingValuationStepProperties,
      [ValuationStepName.Locality]: LocalityValuationStepProperties,
      [ValuationStepName.Unit]: UnitValuationStepProperties,
    }

    for (const step of valuationData.steps) {
      const map = stepMap[step.name]
      const stepData: any = {}
      if (Array.isArray(map)) {
        for (const prop of map) {
          const value = getNestedValue(data, [prop], null)
          if (value !== null) {
            stepData[prop] = value
          }
        }
      } else if (step.name === ValuationStepName.Locality) {
        const locationType: LocationType | undefined = getNestedValue(data, [
          'locationType',
        ])
        if (!locationType) {
          continue
        }
        stepData.locationType = locationType
        stepData.location = {}
        const props = getNestedValue(map, [locationType], [])
        for (const prop of props) {
          const value = getNestedValue(data, [prop], null)
          if (value !== null) {
            stepData.location[prop] = value
          }
        }
      } else if (step.name === ValuationStepName.Unit) {
        const propertyType: PropertyType | undefined = getNestedValue(data, [
          'propertyType',
        ])
        if (!propertyType) {
          continue
        }
        stepData.unit = {}
        stepData.propertyPrice = getNestedValue(data, ['propertyPrice'], 0)
        const props = getNestedValue(map, [propertyType], [])
        for (const prop of props) {
          const value = getNestedValue(data, [prop], null)
          if (value !== null) {
            stepData.unit[prop] = value
          }
        }
      }
      step.data = stepData
    }

    return valuationData
  }
}
